import { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Clock,
  User,
  Car,
  MessageSquare,
  Building2,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useGetList, useNotify } from "ra-core";
import { Checkbox } from "@/components/ui/checkbox";
import { useRealEstateContext } from "../providers/RealEstateContext";
import type { Contact } from "../types";
import type { SiteVisit } from "@/types/realEstate";

function phoneFromContact(contact?: Contact | null): string {
  const raw = contact?.phone_jsonb?.[0]?.number;
  return typeof raw === "string" ? raw : "";
}

const TRANSPORT_OPTIONS = [
  { value: "self_pickup", label: "Self pick-up (client vehicle)" },
  { value: "company_cab", label: "Company cab" },
  { value: "arrange_later", label: "Arrange later" },
] as const;

interface ScheduleVisitDrawerProps {
  contactId?: string;
  contactName?: string;
  contactPhone?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: (visit: Partial<SiteVisit>) => void;
  /** Prefill the datetime-local input, e.g. from a calendar day/week click. */
  initialScheduledAt?: string;
}

export const ScheduleVisitDrawer = ({
  contactId: contactIdProp,
  contactName: contactNameProp,
  contactPhone: contactPhoneProp,
  isOpen,
  onOpenChange,
  onSuccess,
  initialScheduledAt,
}: ScheduleVisitDrawerProps) => {
  const {
    addSiteVisit,
    properties,
    salesAgents,
    campaignAudience,
    refreshCampaignAudience,
  } = useRealEstateContext();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = isOpen !== undefined;
  const open = isControlled ? isOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange || (() => {}) : setInternalOpen;

  const needsLeadPick = !contactIdProp;
  const [pickedLeadId, setPickedLeadId] = useState("");

  // Load leads via the same contacts_summary path as the Contacts list (RLS:
  // agent sees own + unassigned). Do not rely only on campaignAudience, which
  // may still be empty if a prior audience refresh failed.
  const { data: contactRows, isPending: contactsPending } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "last_seen", order: "DESC" },
      filter: {},
    },
    { enabled: open && needsLeadPick },
  );

  const leadOptions = useMemo(() => {
    if (contactRows && contactRows.length > 0) {
      return contactRows.map((c) => ({
        id: String(c.id),
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Lead",
        phone: phoneFromContact(c),
      }));
    }
    return campaignAudience.map((l) => ({
      id: String(l.id),
      name: l.name,
      phone: l.phone || "",
    }));
  }, [contactRows, campaignAudience]);

  const pickedLead = leadOptions.find(
    (l) => String(l.id) === String(pickedLeadId),
  );

  const contactId = contactIdProp || (pickedLead ? String(pickedLead.id) : "");
  const contactName = contactNameProp || pickedLead?.name || "Select a lead";
  const contactPhone = contactPhoneProp || pickedLead?.phone || "";

  const [selectedProperty, setSelectedProperty] = useState("");
  const [scheduledAt, setScheduledAt] = useState(initialScheduledAt ?? "");
  const [assignedAgent, setAgent] = useState("");
  const [transport, setTransport] = useState<string>(
    TRANSPORT_OPTIONS[0].value,
  );
  /** Off by default — opening WhatsApp is opt-in after the visit is saved. */
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setScheduledAt(initialScheduledAt ?? "");
      if (needsLeadPick) {
        void refreshCampaignAudience();
      }
    } else {
      setPickedLeadId("");
    }
  }, [open, initialScheduledAt, needsLeadPick, refreshCampaignAudience]);

  useEffect(() => {
    if (!selectedProperty && properties[0]?.id) {
      setSelectedProperty(String(properties[0].id));
    }
  }, [properties, selectedProperty]);

  useEffect(() => {
    if (!assignedAgent && salesAgents[0]?.id != null) {
      setAgent(String(salesAgents[0].id));
    }
  }, [salesAgents, assignedAgent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactId || !selectedProperty || saving) return;

    const transportLabel =
      TRANSPORT_OPTIONS.find((t) => t.value === transport)?.label || transport;
    const noteParts = [notes.trim(), `Transport: ${transportLabel}`].filter(
      Boolean,
    );

    const newVisit: Partial<SiteVisit> = {
      contact_id: contactId,
      property_id: selectedProperty,
      agent_id: assignedAgent || undefined,
      scheduled_at: scheduledAt
        ? new Date(scheduledAt).toISOString()
        : new Date().toISOString(),
      status: "Scheduled",
      feedback_notes: noteParts.join(" · ") || undefined,
    };

    setSaving(true);
    try {
      const result = await addSiteVisit(
        newVisit,
        contactPhone,
        sendWhatsApp && !!contactPhone,
      );
      if (!result.ok) {
        notify(result.error || "Could not schedule visit", { type: "warning" });
        return;
      }
      if (!result.persisted) {
        notify(
          result.error ||
            "Visit saved locally only — it may not appear after refresh.",
          { type: "warning" },
        );
      }
      onSuccess?.(newVisit);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <SheetTrigger asChild>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Calendar className="w-4 h-4 mr-2" /> Schedule Site Visit
          </Button>
        </SheetTrigger>
      )}

      <SheetContent className="w-full sm:max-w-[540px] overflow-y-auto">
        <SheetHeader className="pb-4 pr-8 border-b">
          <SheetTitle className="flex items-center gap-2 text-xl font-bold">
            <Calendar className="w-5 h-5 text-primary" />
            Schedule Site Visit
          </SheetTitle>
          <SheetDescription>
            Book a project site visit for <strong>{contactName}</strong>
            {contactPhone ? (
              <>
                {" "}
                (<span className="font-mono text-xs">{contactPhone}</span>)
              </>
            ) : null}{" "}
            and optionally dispatch WhatsApp details.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5 py-6">
          {needsLeadPick && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary" /> Lead / Contact
              </label>
              <Select value={pickedLeadId} onValueChange={setPickedLeadId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select lead" />
                </SelectTrigger>
                <SelectContent>
                  {contactsPending && leadOptions.length === 0 ? (
                    <SelectItem value="__loading" disabled>
                      Loading contacts…
                    </SelectItem>
                  ) : leadOptions.length === 0 ? (
                    <SelectItem value="__none" disabled>
                      No contacts available for your desk
                    </SelectItem>
                  ) : (
                    leadOptions.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.name}
                        {lead.phone ? ` · ${lead.phone}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-primary" /> Target Property
              / Project
            </label>
            <Select
              value={selectedProperty}
              onValueChange={setSelectedProperty}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select property unit" />
              </SelectTrigger>
              <SelectContent>
                {properties.length === 0 ? (
                  <SelectItem value="__none" disabled>
                    No properties available
                  </SelectItem>
                ) : (
                  properties.map((prop) => (
                    <SelectItem key={prop.id} value={String(prop.id)}>
                      {prop.project?.name || "Project"} — Unit{" "}
                      {prop.unit_number} ({prop.configuration})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-primary" /> Date & Time of
              Visit
            </label>
            <Input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary" /> Sales Executive
              </label>
              <Select value={assignedAgent} onValueChange={setAgent}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {salesAgents.length === 0 ? (
                    <SelectItem value="__none" disabled>
                      No sales agents
                    </SelectItem>
                  ) : (
                    salesAgents.map((agent) => (
                      <SelectItem
                        key={String(agent.id)}
                        value={String(agent.id)}
                      >
                        {agent.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Car className="w-3.5 h-3.5 text-primary" /> Transport
              </label>
              <Select value={transport} onValueChange={setTransport}>
                <SelectTrigger>
                  <SelectValue placeholder="Transport" />
                </SelectTrigger>
                <SelectContent>
                  {TRANSPORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-primary" /> Visit Notes
              & Client Specifics
            </label>
            <Input
              placeholder="e.g. Visiting with family, prefers Vastu-facing units"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="whatsapp"
                checked={sendWhatsApp}
                disabled={!contactPhone}
                onCheckedChange={(c) => setSendWhatsApp(c === true)}
              />
              <div className="grid gap-1 leading-none">
                <label
                  htmlFor="whatsapp"
                  className="text-xs font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5 text-primary" />
                  Open WhatsApp to send visit invite
                </label>
                <p className="text-[11px] text-muted-foreground">
                  {contactPhone
                    ? "After the visit is saved, opens WhatsApp with a prefilled invite (location + time). Leave unchecked to schedule only."
                    : "Add a phone number on the lead to enable the invite."}
                </p>
              </div>
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t">
            <Button
              variant="outline"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!contactId || !selectedProperty || saving}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {saving
                ? "Scheduling…"
                : sendWhatsApp && contactPhone
                  ? "Schedule & open WhatsApp invite"
                  : "Schedule visit"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default ScheduleVisitDrawer;
